import { For, Show } from "solid-js";
import { useChatRuntime } from "../../runtime/ChatRuntime";
import type { GitHubIssue } from "./githubIssuesSchema";

// URLs arrive from the customer's installation and are untrusted; only https is
// safe to put in an href.
export const safeGitHubUrl = (url: string | undefined): string | undefined =>
  url?.startsWith("https://") ? url : undefined;

// GitHub reads a closed issue's reason as part of its state: resolved and
// declined are both "closed" to the API and different outcomes to a reader.
const statePill = (state: string, stateReason: string | undefined): { class: string; labelKey: string } => {
  if (state === "open") {
    return {
      class: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
      labelKey: "github_state.open",
    };
  }
  if (stateReason === "not_planned") {
    return {
      class: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
      labelKey: "github_state.not_planned",
    };
  }
  return {
    class: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
    labelKey: "github_state.closed",
  };
};

export const GitHubIssueMeta = (props: { issue: GitHubIssue }) => {
  const rt = useChatRuntime();
  const pill = () => statePill(props.issue.state, props.issue.stateReason);

  return (
    <div class="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-text">
      <span class={`shrink-0 rounded px-1.5 py-0.5 ${pill().class}`}>{rt.t(pill().labelKey)}</span>
      <Show when={props.issue.author}>
        {(author) => <span class="truncate">{rt.t("github_issue_meta.author", { author: author() })}</span>}
      </Show>
      <For each={props.issue.assignees}>{(assignee) => <span class="truncate">{assignee}</span>}</For>
      <Show when={props.issue.milestone}>{(milestone) => <span class="truncate">{milestone()}</span>}</Show>
      <For each={props.issue.labels}>
        {(label) => <span class="truncate rounded bg-inset px-1.5 py-0.5">{label}</span>}
      </For>
      <Show when={props.issue.commentCount > 0}>
        <span class="shrink-0">
          {props.issue.commentCount === 1
            ? rt.t("github_issue_meta.comments_one")
            : rt.t("github_issue_meta.comments", { count: String(props.issue.commentCount) })}
        </span>
      </Show>
    </div>
  );
};
